import React, { useState, useEffect } from 'react';
import store, { loanActions } from '@apply/adminStore';
import { LoanApi } from '@apply/api';
import { applicationInsights } from '../../../ApplicationInsights';
import { RefreshEvents } from '../../../common/SharedEvents';
import DataGrid, { Column } from 'devextreme-react/data-grid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil } from '@fortawesome/free-solid-svg-icons';

export const ExtensionsGrid = ({ loanId, maturityDate }) => {
    const { dispatch } = store;
    const loanApi = new LoanApi.LoanApi(applicationInsights);
    const refreshEvents = new RefreshEvents();

    const [extensions, set_extensions] = useState([]);
    const [isEditable, set_isEditable] = useState(false);

    const styles = {
        sectionHeader: { margin: '10px 0' },
        formColumn: { paddingBottom: '5px' },
        formRow: { marginBottom: '0' },
    };

    const loadData = async () => {
        if (!loanId) {
            return;
        }

        const response = await loanApi.getLoanExtensionsGrid(loanId);
        if (response.statusCode !== 200) {
            return;
        }
        set_extensions(response.result.extensions);
        set_isEditable(response.result.isEditable);
    };

    useEffect(() => {
        refreshEvents.addRefreshListener(_afterRefresh);
        return () => {
            refreshEvents.removeRefreshListener(_afterRefresh);
        };
    }, []);

    useEffect(() => {
        // Do we need to await if no code follows?
        async function loanIdChanged() {
            await loadData();
        }

        loanIdChanged();
    }, [loanId]);

    const _afterRefresh = async () => {
        await loadData();
    };

    const editExtension = (event, extensionId) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        dispatch(
            loanActions.setExtensionModal({
                extensionModal: { isOpen: true, loanId, extensionId },
            })
        );
    };

    const actionCellRender = (e) => {
        if (isEditable) {
            return (
                <button
                    id={'action_' + e.data.id}
                    className='gridButton'
                    title='Edit Loan Extension'
                    onClick={(event) => editExtension(event, e.data.id)}
                >
                    <FontAwesomeIcon icon={faPencil} />
                </button>
            );
        } else {
            return null;
        }
    };

    const onPaymentNumberRender = (e) => {
        return e.rowIndex + 1;
    };

    const onMonthsRender = (e) => {
        if (e.data.months === 1) {
            return '1 month';
        } else {
            return <span style={e.data.months < 0 ? { color: 'red' } : null}>{`${e.data.months} months`}</span>;
        }
    };

    const onIsContractual = (e) => {
        return e.data.isContractual ? 'Yes' : 'No';
    };

    const onFeeRender = (e) => {
        return (e.data.feePaid ?? 0).toFixed(2) + '%';
    };

    if (!loanId || !extensions || extensions.length === 0) {
        return '';
    }

    return (
        <div style={{ marginTop: '10px' }}>
            <h2
                className='decorated'
                style={styles.sectionHeader}
            >
                Extensions
            </h2>
            <DataGrid
                dataSource={extensions}
                showBorders={true}
                showRowLines={false}
                showColumnHeaders={true}
                rowAlternationEnabled={true}
                columnMinWidth={50}
                columnAutoWidth={false}
                sorting={false}
                onRowDblClick={(e) => editExtension(null, e.data.id)}
            >
                <Column
                    cellRender={onPaymentNumberRender}
                    caption='#'
                    width={20}
                />
                <Column
                    dataField='extensionDate'
                    caption='Ext. Until'
                    dataType='date'
                    width={100}
                />
                <Column
                    caption='Mnths'
                    width={100}
                    alignment='left'
                    cellRender={onMonthsRender}
                />
                <Column
                    caption='Cntrctl'
                    width={75}
                    alignment='left'
                    cellRender={onIsContractual}
                />
                <Column
                    caption='Fee (%)'
                    width={100}
                    alignment='left'
                    cellRender={onFeeRender}
                />
                <Column
                    alignment='center'
                    cellRender={actionCellRender}
                    width={50}
                    allowSorting={false}
                    allowResizing={false}
                />
            </DataGrid>
        </div>
    );
};
